/* ============================================================================
   Fifth Room — Get a Quote (multi-step lead form) engine   BESPOKE
   <quote-builder> custom element. Paired with snippets/fr-quote-builder.liquid
   + assets/fr-quote-builder.css.

   Linear one-question-per-screen stepper with conditional (visible_when) steps.
   All controls carry their exact Jotform `name` and live inside the delivery
   <form>, which posts into a hidden same-page <iframe> (CORS-safe). On submit we
   inject the computed Pipedrive mirror fields, the size composite, the
   appointment date/timezone, and Jotform's anti-bot tokens, then form.submit().
   We show our own success panel on the iframe `load` (we can't read the
   cross-origin response — we don't need to).
   ============================================================================ */
(function () {
  'use strict';

  var EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  function reducedMotion() {
    try { return window.matchMedia('(prefers-reduced-motion: reduce)').matches; } catch (e) { return false; }
  }

  class QuoteBuilder extends HTMLElement {
    connectedCallback() {
      if (this._init) return;
      this._init = true;

      var cfgEl = this.querySelector('[data-quote-config]');
      try { this.config = JSON.parse(cfgEl.textContent); } catch (e) { this.config = { steps: [] }; }
      this.steps = this.config.steps || [];
      this.byId = {};
      this.steps.forEach(function (s) { this.byId[s.id] = s; }, this);

      this.form = this.querySelector('.qb-form');
      this.stepEls = Array.prototype.slice.call(this.querySelectorAll('.qb-step'));
      this.stepElById = {};
      this.stepEls.forEach(function (el) { this.stepElById[el.dataset.stepId] = el; }, this);

      this.backBtn = this.querySelector('[data-qb-back]');
      this.nextBtn = this.querySelector('[data-qb-next]');
      this.progressFill = this.querySelector('[data-qb-progress-fill]');
      this.countEl = this.querySelector('[data-qb-stepcount]');
      this.formError = this.querySelector('[data-qb-formerror]');
      this.successEl = this.querySelector('[data-qb-success]');
      this.startBtn = this.querySelector('[data-qb-start]');

      this._slot = '';
      this._lastCount = '';
      this._done = false;

      // Intro "Start" stage: while a hero + Start button exist, the form/progress/nav stay
      // hidden (CSS, on :not([data-qb-started])) until the user clicks Start. With no Start
      // button (intro banner off) we begin in the form stage immediately.
      this._started = this.hasAttribute('data-qb-started');
      if (!this._started && !this.startBtn) { this._started = true; this.setAttribute('data-qb-started', ''); }

      var firstVisible = this._visibleList()[0] || this.steps[0];
      this.currentId = firstVisible ? firstVisible.id : null;

      this._buildSlots();
      this._initApptDate();
      this._bind();
      this._render({ focus: false });
    }

    /* ── value helpers ──────────────────────────────────────────────────── */
    _val(name) {
      if (!name) return '';
      var nodes = this.form.querySelectorAll('[name="' + (window.CSS && CSS.escape ? CSS.escape(name) : name) + '"]');
      if (!nodes.length) return '';
      if (nodes[0].type === 'radio') {
        for (var i = 0; i < nodes.length; i++) { if (nodes[i].checked) return (nodes[i].value || '').trim(); }
        return '';
      }
      return (nodes[0].value || '').trim();
    }

    // normalized answer for visible_when comparisons
    _answerFor(id) {
      var step = this.byId[id];
      if (!step) return '';
      if (step.type === 'image-radio') {
        var v = this._val(step.name);
        return v ? v.split('|')[0] : '';
      }
      if (step.type === 'yesno' || step.type === 'dropdown') return this._val(step.name);
      return '';
    }

    _isVisible(step) {
      if (!step.visible_when) return true;
      var rules = step.visible_when;
      for (var depId in rules) {
        if (!Object.prototype.hasOwnProperty.call(rules, depId)) continue;
        var rule = rules[depId];
        var ans = this._answerFor(depId);
        if (rule.in && rule.in.indexOf(ans) === -1) return false;
        if (rule.not_in && rule.not_in.indexOf(ans) !== -1) return false;
      }
      return true;
    }

    _visibleList() {
      return this.steps.filter(this._isVisible, this);
    }

    /* ── events ─────────────────────────────────────────────────────────── */
    _bind() {
      var self = this;

      this.addEventListener('click', function (e) {
        if (e.target.closest('[data-qb-start]')) { if (!e.isTrusted) return; self._start(); return; }
        if (e.target.closest('[data-qb-back]')) { if (!e.isTrusted) return; self._goPrev(); return; }
        if (e.target.closest('[data-qb-next]')) { if (!e.isTrusted) return; self._next(); return; }
        var slot = e.target.closest('.qb-slot');
        if (slot) { self._pickSlot(slot); return; }
      });

      this.addEventListener('change', function (e) {
        if (e.target.matches('[data-qb-appt-date]')) { self._composeApptPreview(); }
        // a change may flip a conditional step in/out of the flow → re-sync (no focus move)
        self._render({ focus: false });
        self._clearError(self.stepElById[self.currentId]);
      });

      // Enter in a text-like field advances instead of submitting the page
      this.addEventListener('keydown', function (e) {
        if (e.key !== 'Enter') return;
        var t = e.target;
        if (t && (t.tagName === 'INPUT') && t.type !== 'file') {
          e.preventDefault();
          self._next();
        }
      });

      // we POST via fetch — never let a native form submit/navigate; Enter advances instead
      this.form.addEventListener('submit', function (e) { e.preventDefault(); self._next(); });
    }

    /* ── navigation ─────────────────────────────────────────────────────── */
    _start() {
      if (this._started) return;
      this._started = true;
      this.setAttribute('data-qb-started', '');   // CSS reveals the form + hides the intro
      this._render({ focus: true });              // focus the first question, scroll into view
    }

    _next() {
      var cur = this.byId[this.currentId];
      var curEl = this.stepElById[this.currentId];
      var v = this._validate(cur, curEl);
      if (!v.ok) { this._showError(curEl, v.message); this._focusFirstControl(curEl); return; }
      this._clearError(curEl);

      var vis = this._visibleList();
      var i = this._indexIn(vis, this.currentId);
      if (i > -1 && i < vis.length - 1) {
        this.currentId = vis[i + 1].id;
        this._render({ focus: true });
      } else {
        this._submit();
      }
    }

    _goPrev() {
      var vis = this._visibleList();
      var i = this._indexIn(vis, this.currentId);
      if (i > 0) { this.currentId = vis[i - 1].id; this._render({ focus: true }); }
    }

    _indexIn(list, id) {
      for (var i = 0; i < list.length; i++) { if (list[i].id === id) return i; }
      return -1;
    }

    /* ── render (idempotent) ────────────────────────────────────────────── */
    _render(opts) {
      opts = opts || {};
      var vis = this._visibleList();
      var visIds = {};
      vis.forEach(function (s) { visIds[s.id] = true; });

      // self-heal: if the current step fell out of the flow, snap to first visible
      if (!visIds[this.currentId]) { this.currentId = vis.length ? vis[0].id : this.currentId; }

      this.stepEls.forEach(function (el) {
        var id = el.dataset.stepId;
        var inFlow = !!visIds[id];
        var isCurrent = id === this.currentId;
        // Jotform re-validates required fields server-side and needs every field PRESENT in the
        // POST (empty is fine for conditionally-hidden ones, but ABSENT → "Incomplete Values").
        // So we keep all controls submittable and just CLEAR out-of-flow steps, so they post
        // empty rather than with stale values.
        if (!inFlow) this._clearStep(el);
        el.hidden = !isCurrent;
        el.inert = !isCurrent;
      }, this);

      var N = vis.length;
      var X = Math.max(1, this._indexIn(vis, this.currentId) + 1);
      if (this.progressFill) this.progressFill.style.width = (N ? (X / N) * 100 : 0) + '%';
      var txt = 'Step ' + X + ' of ' + N;
      if (this.countEl && txt !== this._lastCount) { this.countEl.textContent = txt; this._lastCount = txt; }

      var isLast = X >= N;
      if (this.nextBtn) this.nextBtn.innerHTML = isLast ? 'Get my quote &check;' : 'Continue &rarr;';
      if (this.backBtn) this.backBtn.hidden = X <= 1;
      if (this.formError) this.formError.textContent = '';

      if (opts.focus) {
        var curEl = this.stepElById[this.currentId];
        var legend = curEl && curEl.querySelector('.qb-step__legend');
        this._scrollIntoViewSmart(curEl);   // measure first (before the entrance transform)
        if (legend && legend.focus) { try { legend.focus({ preventScroll: true }); } catch (e) { legend.focus(); } }
        this._animateIn(curEl);              // then fade/slide the new card in
      }
    }

    _clearStep(el) {
      // uncheck radios, blank text/date inputs and selects; leave type=hidden constants
      // (appointment implementation/duration/timezone, address country) intact so they post.
      el.querySelectorAll('input[type=radio]').forEach(function (r) { r.checked = false; });
      el.querySelectorAll('input:not([type=hidden]):not([type=radio]):not([type=file])').forEach(function (i) { i.value = ''; });
      el.querySelectorAll('select').forEach(function (s) { s.value = ''; });
      var file = el.querySelector('input[type=file]'); if (file) file.value = '';
      if (el.dataset.stepId === 'survey_datetime') {
        this._slot = '';
        el.querySelectorAll('.qb-slot').forEach(function (b) { b.setAttribute('aria-pressed', 'false'); });
        var tzNote = el.querySelector('[data-qb-appt-tz]'); if (tzNote) tzNote.textContent = '';
      }
    }

    _scrollTo(el) {
      if (!el) return;
      try { el.scrollIntoView({ behavior: reducedMotion() ? 'auto' : 'smooth', block: 'start' }); }
      catch (e) { el.scrollIntoView(); }
    }

    // Replay the card's fade/slide-in, restarting the CSS animation on every step change.
    _animateIn(el) {
      if (!el || reducedMotion()) return;
      el.classList.remove('qb-step--enter');
      void el.offsetWidth;            // force reflow so the animation restarts cleanly
      el.classList.add('qb-step--enter');
    }

    // Smoothly bring the new card into view — but ONLY when it isn't already comfortably
    // visible, so short consecutive cards don't make the page lurch on every Continue.
    _scrollIntoViewSmart(el) {
      if (!el) return;
      var offset = this._headerOffset();
      var rect = el.getBoundingClientRect();
      var vh = window.innerHeight || document.documentElement.clientHeight;
      // legend already sitting below the header and within the upper part of the screen → leave it
      if (rect.top >= offset - 8 && rect.top <= vh * 0.6) return;
      var y = (window.pageYOffset || document.documentElement.scrollTop || 0) + rect.top - offset;
      if (y < 0) y = 0;
      try { window.scrollTo({ top: y, behavior: reducedMotion() ? 'auto' : 'smooth' }); }
      catch (e) { window.scrollTo(0, y); }
    }

    // Height of the sticky header (so a scroll never tucks the card underneath it).
    // Returns a small breathing gap when no sticky/fixed header is found.
    _headerOffset() {
      var node = document.getElementById('header-component') || document.querySelector('.header, header');
      var stickyH = 0;
      while (node && node !== document.body) {
        var pos = '';
        try { pos = getComputedStyle(node).position; } catch (e) {}
        if (pos === 'sticky' || pos === 'fixed') { stickyH = node.getBoundingClientRect().height || 0; break; }
        node = node.parentElement;
      }
      if (stickyH > 0 && stickyH < 260) return stickyH + 16;
      return 20;
    }

    _focusFirstControl(el) {
      var c = el.querySelector('input:not([type=hidden]), select, textarea, .qb-choice__input, button.qb-slot');
      if (c && c.focus) c.focus();
    }

    /* ── validation ─────────────────────────────────────────────────────── */
    _validate(step, el) {
      var ok = { ok: true };
      var err = function (m) { return { ok: false, message: m }; };
      switch (step.type) {
        case 'image-radio':
          return this._val(step.name) ? ok : err('Please choose an option to continue.');
        case 'dropdown':
          return this._val(step.name) ? ok : err('Please make a selection to continue.');
        case 'yesno':
          return this._val(step.name) ? ok : err('Please choose an option to continue.');
        case 'size':
          return (this._val(step.width_name) && this._val(step.depth_name)) ? ok : err('Please choose a width and depth.');
        case 'address': {
          var pc = (el.querySelector('[name="q80_address[postal]"]') || {}).value || '';
          return pc.trim().length >= 4 ? ok : err('Please enter a valid postcode.');
        }
        case 'appointment': {
          var d = (el.querySelector('[data-qb-appt-date]') || {}).value || '';
          return (d && this._slot) ? ok : err('Please pick a preferred date and time.');
        }
        case 'name': {
          var f = (el.querySelector('[name="q18_name[first]"]') || {}).value || '';
          var l = (el.querySelector('[name="q18_name[last]"]') || {}).value || '';
          return (f.trim() && l.trim()) ? ok : err('Please enter your first and last name.');
        }
        case 'email': {
          var em = this._val(step.name);
          return EMAIL_RE.test(em) ? ok : err('Please enter a valid email address.');
        }
        case 'tel': {
          var digits = this._val(step.name).replace(/[^0-9]/g, '');
          return digits.length >= 7 ? ok : err('Please enter a valid phone number.');
        }
        case 'file':
          return ok;
        default:
          return ok;
      }
    }

    _showError(el, msg) {
      var p = el && el.querySelector('[data-qb-error]');
      if (p) p.textContent = msg || '';
    }
    _clearError(el) { this._showError(el, ''); }

    /* ── appointment picker ─────────────────────────────────────────────── */
    _buildSlots() {
      var host = this.querySelector('[data-qb-appt-slots]');
      if (!host) return;
      var html = '';
      for (var m = 9 * 60; m <= 16 * 60 + 30; m += 30) {
        var hh = Math.floor(m / 60), mm = m % 60;
        var val = (hh < 10 ? '0' + hh : hh) + ':' + (mm === 0 ? '00' : mm);
        var ampm = hh < 12 ? 'AM' : 'PM';
        var h12 = ((hh + 11) % 12) + 1;
        var disp = h12 + ':' + (mm === 0 ? '00' : mm) + ' ' + ampm;
        html += '<button type="button" class="qb-slot" data-val="' + val + '" aria-pressed="false">' + disp + '</button>';
      }
      host.innerHTML = html;
    }

    _initApptDate() {
      var input = this.querySelector('[data-qb-appt-date]');
      if (!input) return;
      var t = new Date();
      t.setDate(t.getDate() + 1); // earliest = tomorrow
      input.min = t.getFullYear() + '-' + this._p2(t.getMonth() + 1) + '-' + this._p2(t.getDate());
    }

    _pickSlot(btn) {
      this._slot = btn.dataset.val || '';
      var slots = this.querySelectorAll('.qb-slot');
      for (var i = 0; i < slots.length; i++) { slots[i].setAttribute('aria-pressed', slots[i] === btn ? 'true' : 'false'); }
      this._composeApptPreview();
      this._clearError(this.stepElById['survey_datetime']);
    }

    _composeApptPreview() {
      var step = this.stepElById['survey_datetime'];
      if (!step) return;
      var dEl = step.querySelector('[data-qb-appt-date]');
      var tzNote = step.querySelector('[data-qb-appt-tz]');
      if (!dEl || !dEl.value) { if (tzNote) tzNote.textContent = ''; return; }
      if (tzNote) tzNote.textContent = 'Times are shown in UK time (' + this._londonTz(dEl.value) + ').';
    }

    _londonTz(dateStr) {
      try {
        var d = new Date(dateStr + 'T12:00:00');
        var parts = new Intl.DateTimeFormat('en-GB', { timeZone: 'Europe/London', timeZoneName: 'longOffset' }).formatToParts(d);
        var raw = '';
        parts.forEach(function (p) { if (p.type === 'timeZoneName') raw = p.value; });
        var off = (raw || '').replace('GMT', '').trim();
        if (!/^[+-]\d\d:\d\d$/.test(off)) off = '+00:00';
        return 'Europe/London (GMT' + off + ')';
      } catch (e) { return 'Europe/London (GMT+00:00)'; }
    }

    _p2(n) { return n < 10 ? '0' + n : '' + n; }

    /* ── submit ─────────────────────────────────────────────────────────── */
    _submit() {
      var vis = this._visibleList();
      // full sweep — a conditional step may have been completed then re-shown
      for (var i = 0; i < vis.length; i++) {
        var el = this.stepElById[vis[i].id];
        var v = this._validate(vis[i], el);
        if (!v.ok) {
          this.currentId = vis[i].id;
          this._render({ focus: true });
          this._showError(el, v.message);
          return;
        }
      }
      this._injectComputed(vis);

      // Serialise the form (every field now present; out-of-flow ones empty) and POST via fetch.
      // Jotform's submit endpoint allows cross-origin reads, so we can detect real success/failure.
      var params = new URLSearchParams();
      new FormData(this.form).forEach(function (val, key) { if (typeof val === 'string') params.append(key, val); });

      var self = this;
      var wrap = this.closest('.fr-quote') || this;
      wrap.classList.add('is-submitting');
      if (this.nextBtn) this.nextBtn.disabled = true;
      if (this.formError) this.formError.textContent = '';

      fetch(this.config.endpoint, {
        method: 'POST',
        body: params,
        headers: { 'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8' }
      }).then(function (res) {
        return res.text().then(function (t) { return { ok: res.ok, text: t }; }, function () { return { ok: res.ok, text: '' }; });
      }).then(function (r) {
        // Jotform: HTTP 400 "Incomplete Values" on a validation miss, 200 "Thank You!" on success
        if (r.ok && !/Incomplete Values/i.test(r.text)) { self._showSuccess(); }
        else { self._submitFailed(); }
      }).catch(function () {
        // opaque/network error — the POST may still have landed; be optimistic rather than lose the lead
        self._showSuccess();
      });
    }

    _submitFailed() {
      var wrap = this.closest('.fr-quote') || this;
      wrap.classList.remove('is-submitting');
      if (this.nextBtn) this.nextBtn.disabled = false;
      if (this.formError) this.formError.textContent = "Sorry, something went wrong sending your enquiry. Please try again, or call us on 0161 524 9932.";
    }

    _injectComputed(vis) {
      var self = this;
      var visIds = {};
      vis.forEach(function (s) { visIds[s.id] = true; });
      // clear any computed fields from a prior attempt
      this.form.querySelectorAll('[data-qb-computed]').forEach(function (n) { n.remove(); });
      var add = function (name, value) {
        var inp = document.createElement('input');
        inp.type = 'hidden';
        inp.name = name;
        inp.value = value == null ? '' : value;
        inp.setAttribute('data-qb-computed', '');
        self.form.appendChild(inp);
      };

      this.steps.forEach(function (s) {
        var inFlow = !!visIds[s.id];
        if (inFlow) {
          // Pipedrive mirror fields (Jotform's own JS isn't running, so we fill them)
          if (s.mirror) {
            if (s.type === 'image-radio') {
              add(s.mirror, (self._val(s.name).split('|')[0]) || '');
            } else if (s.type === 'yesno' && s.mirror_map) {
              var yv = self._val(s.name);
              add(s.mirror, (yv && s.mirror_map[yv]) || '');
            }
          }
          if (s.type === 'size') {
            add(s.mirror, self._val(s.width_name) + ' (Width) by ' + self._val(s.depth_name) + ' (Depth)');
          }
          if (s.type === 'appointment') {
            var step = self.stepElById[s.id];
            var dEl = step.querySelector('[data-qb-appt-date]');
            var dateField = step.querySelector('[data-qb-appt-date-field]');
            var tzField = step.querySelector('[data-qb-appt-tz-field]');
            if (dEl && dEl.value && self._slot) {
              if (dateField) dateField.value = dEl.value + ' ' + self._slot;   // "YYYY-MM-DD HH:MM"
              if (tzField) tzField.value = self._londonTz(dEl.value);
            }
          }
        } else {
          // out of flow → field must be PRESENT but empty. Unchecked radio groups submit
          // nothing, so send an explicit empty value for image-radio steps + their mirror.
          if (s.type === 'image-radio' && s.name) add(s.name, '');
          if (s.mirror) add(s.mirror, '');
        }
      });
    }

    _showSuccess() {
      if (this._done) return;
      this._done = true;
      var wrap = this.closest('.fr-quote') || this;
      wrap.classList.remove('is-submitting');
      if (this.form) this.form.hidden = true;
      var nav = this.querySelector('.qb-nav'); if (nav) nav.hidden = true;
      var prog = this.querySelector('.qb-progress'); if (prog) prog.hidden = true;
      var hero = wrap.querySelector('.fr-quote__hero'); if (hero) hero.hidden = true;
      if (this.successEl) {
        this.successEl.hidden = false;
        if (this.successEl.focus) { try { this.successEl.setAttribute('tabindex', '-1'); this.successEl.focus(); } catch (e) {} }
        this._scrollTo(this.successEl);
      }
    }
  }

  if (!customElements.get('quote-builder')) {
    customElements.define('quote-builder', QuoteBuilder);
  }
})();
